import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router';

import getMutationUrl from '@/routes/routes/helpers/getMutationUrl';
import { eventFullPropTypeShape } from '@/proptypes';

import Tooltip from '../Tooltip';

import {
  container,
  heartButtonText,
  heartButtonTextSaved,
  heartContainer,
  blackHeart,
  redHeart,
  whiteHeart,
  whiteAndRedHeart,
} from './styles.module.css';

const getTooltipText = (saved) => (saved ? 'Remove' : 'Save');

const HeartButton = ({
  id,
  black,
  saved,
  children,
  className,
  onClick,
  onMouseEnter,
  onMouseLeave,
  loggedIn,
  showText,
  tooltipVisible,
  loginReturnUrl,
}) => {
  let heartColor;

  if (black) {
    if (saved) {
      heartColor = redHeart;
    } else {
      heartColor = blackHeart;
    }
  } else if (saved) {
    heartColor = whiteAndRedHeart;
  } else {
    heartColor = whiteHeart;
  }

  const mutatorVariables = {
    id,
    returnUrl: loginReturnUrl,
  };

  return (
    <div className={container}>
      <div className={heartContainer}>
        {tooltipVisible && (
          <Tooltip
            text={getTooltipText(saved)}
            position={Tooltip.position.ABOVE}
          />
        )}
        {loggedIn ? (
          <button
            id={id}
            className={[heartColor, className].join(' ')}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            {children}
            {showText && (
              <div
                className={[heartButtonText]
                  .concat(saved ? heartButtonTextSaved : [])
                  .join(' ')}
              >
                {saved ? 'Saved' : 'Save'}
              </div>
            )}
          </button>
        ) : (
          <Link
            rel="nofollow"
            to={getMutationUrl(
              saved ? '/unsave-event' : '/save-event',
              mutatorVariables,
            )}
            className={[heartColor, className].join(' ')}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            {children}
            {showText && (
              <div
                className={[heartButtonText]
                  .concat(saved ? heartButtonTextSaved : [])
                  .join(' ')}
              >
                Save
              </div>
            )}
          </Link>
        )}
      </div>
    </div>
  );
};

export default HeartButton;

HeartButton.propTypes = {
  /**
   * Boolean to describe initial color of unsaved heart if black (i.e. on event page).
   */
  black: PropTypes.bool,
  /**
   * Children contents of button.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  /**
   * String class names to append to component styles.
   */
  className: PropTypes.string,
  /**
   * Numeric ID of the event.
   */
  id: eventFullPropTypeShape.id, // eslint-disable-line react/require-default-props
  /**
   * Boolean describing user autenticated state.
   */
  loggedIn: PropTypes.bool,
  /**
   * Function called on hovering over Save button. Fires tooltip.
   */
  onMouseEnter: PropTypes.func,
  /**
   * Function called on hovering off Save button. Fires tooltip.
   */
  onMouseLeave: PropTypes.func,
  /**
   * Bool to describe visibility of tooltip when hovering over 'Save' button.
   */
  tooltipVisible: PropTypes.bool,
  /**
   * Bool to describe whether user has saved event to their wishlist.
   */
  saved: PropTypes.bool,
  /**
   * Boolean to descrive whether 'Save Event' / 'Saved' should render within button.
   */
  showText: PropTypes.bool,
  /**
   * Callback function when user clicks on heart button.
   */
  onClick: PropTypes.func,
  /**
   * String to return the user to after login wall
   */
  loginReturnUrl: PropTypes.string,
};

HeartButton.defaultProps = {
  black: false,
  className: '',
  id: undefined,
  saved: undefined,
  loggedIn: undefined,
  showText: false,
  tooltipText: 'Save Event',
  tooltipVisible: undefined,
  loginReturnUrl: undefined,
  onClick: () => {},
  onMouseEnter: () => {},
  onMouseLeave: () => {},
};
